/**
 * Created by pyt on 3/8/17.
 */
import axios from 'axios';

import { FilterCollection } from '@/common/models';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { generateUUID } from '@/globals';

export type Action<T> = ({
  params,
  queryParams,
  showLoader,
}: {
  params?: QueryParams;
  queryParams?: QueryParams;
  showLoader: boolean;
}) => Promise<T>;

class QueryParams {
  id: string;
  top: number;
  skip: number;
  filterCollection: FilterCollection;
  sort: string;
  returnsCount: boolean;
  parentID: string;
  expand: Array<string>;
  expandStrict: Array<string>;
  showLoader: boolean;
  include: Array<string>;
  customQueryParams: Record<string, any>;
  cancelTokenSource: any;
  static MAXIMUM_BULK_UPDATE_RECORD_COUNT: number = 5000;

  constructor(
    top = 20,
    skip = 0,
    filterCollection = null,
    sort = null,
    returnsCount = true,
    expand = null, // Array
    expandStrict = null, // Array
    include = null,
    customQueryParams = {},
  ) {
    this.id = generateUUID();
    this.top = top;
    this.skip = skip;
    this.filterCollection = filterCollection;
    this.sort = sort;
    this.returnsCount = returnsCount;
    this.parentID = null;
    this.expand = expand;
    this.expandStrict = expandStrict;
    this.showLoader = true;
    this.include = include;
    this.customQueryParams = customQueryParams;
    this.cancelTokenSource = axios.CancelToken.source();
    //
    // !!! Modify the clone method when adding new parameters.
    //
  }

  clone(copyID = false) {
    const qp = new QueryParams(
      this.top,
      this.skip,
      this.filterCollection,
      this.sort,
      this.returnsCount,
      this.expand,
      this.include,
      this.customQueryParams,
    );
    if (copyID) {
      qp.id = this.id;
    }
    return qp;
  }

  async run<T>(action: Action<T>, maxQueryLength = 9500): Promise<T> {
    if (this.toQueryString().length > maxQueryLength) {
      GlobalEventBus.$emit(GlobalEvents.requestUriTooLong);
      return null;
    }
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel();
      this.cancelTokenSource = axios.CancelToken.source();
    }
    return action({ params: this, queryParams: this, showLoader: this.showLoader });
  }

  toQueryString(): string {
    let queryString = `?$count=${this.returnsCount}`;
    if (this.top != null) {
      queryString = `${queryString}&$top=${this.top}`;
    }
    if (this.skip != null) {
      queryString = `${queryString}&$skip=${this.skip}`;
    }
    if (this.sort != null) {
      queryString = `${queryString}&$orderby=${this.sort}`;
    }
    if (this.filterCollection != null && !this.filterCollection.isEmpty) {
      queryString = `${queryString}&${this.filterCollection.toQueryParam()}`;
    }
    if (this.expand != null && this.expand.length > 0) {
      queryString = `${queryString}&$expand=${this.expand.join(',')}`;
    }
    if (this.expandStrict != null && this.expandStrict.length > 0) {
      queryString = `${queryString}&$expandstrict=${this.expandStrict.join(',')}`;
    }
    if (this.include != null && this.include.length > 0) {
      queryString = `${queryString}&$select=${this.include.join(',')}`;
    }
    if (this.customQueryParams != null) {
      Object.entries(this.customQueryParams).forEach((x) => {
        queryString = `${queryString}&${x[0]}=${x[1]}`;
      });
    }
    return queryString;
  }

  toObject() {
    let params = {
      $count: this.returnsCount,
      $top: this.top !== null ? this.top : undefined,
      $skip: this.skip !== null ? this.skip : undefined,
      $orderby: this.sort !== null ? this.sort : undefined,
      $expand: this.expand !== null && this.expand.length > 0 ? this.expand.join(',') : undefined,
      $expandstrict:
        this.expandStrict !== null && this.expandStrict.length > 0 ? this.expandStrict.join(',') : undefined,
      $select: this.include !== null && this.include.length > 0 ? this.include.join(',') : undefined,
      $filter:
        this.filterCollection !== null && this.filterCollection.expression !== ''
          ? this.filterCollection.expression
          : undefined,
    };
    params = {
      ...params,
      ...(this.customQueryParams != null ? this.customQueryParams : {}),
    };
    return params;
  }

  toGetOnePutPostObject() {
    const params = {
      $expand: this.expand !== null && this.expand.length > 0 ? this.expand.join(',') : undefined,
      $select: this.include !== null && this.include.length > 0 ? this.include.join(',') : undefined,
    };

    if (this.customQueryParams !== null) {
      return {
        ...params,
        ...this.customQueryParams,
      };
    }

    return params;
  }

  static for({
    top = 20,
    skip = 0,
    filterCollection = null,
    sort = null,
    returnsCount = true,
    expand = null,
    expandStrict = null,
    include = null,
    customQueryParams = {},
  } = {}) {
    return new QueryParams(
      top,
      skip,
      filterCollection,
      sort,
      returnsCount,
      expand,
      expandStrict,
      include,
      customQueryParams,
    );
  }

  static all(filterCollection = null, include = null) {
    return QueryParams.for({ top: null, filterCollection, include });
  }
}

export default QueryParams;
